import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Textarea from "components/atoms/Textarea";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Text from "components/atoms/text/Text";

import ReferenceBibInfoEditModal from "components/page/modal/ReferenceBibInfoEditModal";
import ReferenceCitationStyleModal from "components/page/modal/ReferenceCitationStyleModal";
import NavigatorFolderEditModal from "components/page/modal/NavigatorFolderEditModal";
import ReferenceTagEditModal from "components/page/modal/ReferenceTagEditModal";
import ReferenceDetailPageHeader from "components/organisms/reference/ReferenceDetailPageHeader";
import ReferenceDetailPageLayout from "components/organisms/reference/ReferenceDetailPageLayout";
import ReferenceFileAnalysisView from "components/organisms/reference/ReferenceFileAnalysisView";
import ReferenceFileUploadView from "components/organisms/reference/ReferenceFileUploadView";
import ReferenceDetailSidebar from "components/organisms/reference/ReferenceDetailSidebar";
import ReferenceFilePdfView from "components/organisms/reference/ReferenceFilePdfView";
import SpaceDetailPageLayout from "components/templates/SpaceDetailPageLayout";

import { useReferenceList, useReference, useReferenceDelete, useReferenceUpdate } from "hooks/queries/useReference";
import { useNavigatorFolders } from "hooks/queries/useNavigator";
import { useUser } from "hooks/queries/useUser";
import { useReferenceReviewWebSocket } from "hooks/useReferenceReviewWebSocket";

import Constants from "utils/constants";
import { getDateFromString, getTimeFromString } from "utils/dateUtil";
import { isAdminUser, isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

const DEFAULT_REFERENCE_LIST_PARAMS = {
    page: 1,
    page_size: 100,
    ordering: "-created_at",
};

const PageLayout = styled.div`
    position: relative;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
`;

const ReviewMainInner = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
`;

const ReviewToolbar = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 6px 12px;
    border-bottom: 1px solid var(--color-Line);
    background-color: var(--color-White);
    flex-shrink: 0;
    z-index: 20;
    overflow: visible;
`;

const ReviewToolbarLeft = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    z-index: 1;
`;

const ReviewToolbarCenter = styled.div`
    position: absolute;
    left: 50%;
    top: 50%;
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 6px;
    transform: translate(-50%, -50%);
`;

const ReviewToolbarRight = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 6px;
    margin-left: auto;
    z-index: 1;
`;

const ReviewViewContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const PdfReaderContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const ReferenceDetailPreview = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;
    padding: 24px 28px 64px;
    background-color: var(--color-Background);
`;

const ReferenceDetailPreviewInner = styled.div`
    width: min(100%, 920px);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const DetailCard = styled.section`
    width: 100%;
    padding: 28px 32px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    height: 20px;
    gap: 4px;
    margin-bottom: 8px;
`;

const SectionHeaderTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const BibInfoSection = styled.div`
    margin: 0;
`;

const BibInfoText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    display: block;
`;

const MetadataExtractionStatus = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const AbstractSection = styled.div`
    margin: 0;
    min-height: 32px;
`;

const AbstractTextarea = styled(Textarea)``;

const AnnotationRightSection = styled.div`
    width: 100%;
    max-width: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ReferenceLinkItem = styled.div`
    width: 100%;
    margin-bottom: 0;
`;

const ReferenceLinkItemHeader = styled(SectionHeader)``;

const ReferenceLinkItemHeaderTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const ReferenceLinkTagItemWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: auto;
    min-height: 20px;
`;

const ReferenceLinkTagItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${(props) => props.$margin || "0"};
    padding: 0px 8px;
    max-width: 180px;
    min-width: 0;
    height: 20px;
    overflow: hidden;
    outline: none;
    background-color: var(--color-Tag);
    border: transparent;
    border-radius: 4px;
`;

const ReferenceLinkItemBlankBox = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    height: 20px;
`;

const ReferenceLinkItemBlankText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MemoSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const MemoTitleText = styled(Text).attrs({
    variant: "body-md-500",
})`
    margin: 0 0 8px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const MemoDiv = styled.div`
    flex: 1;
`;

const MemoTextarea = styled.textarea`
    padding: 4px 6px;
    width: 100%;
    min-height: 100%;
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 2px;
    resize: none;
    outline: none;

    &::placeholder {
        color: var(--color-Grey2);
    }

    &:disabled {
        background-color: var(--color-DisabledInput);
        cursor: not-allowed;
    }
`;

const DateSection = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const DateText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const ReferenceListSection = styled.div`
    width: 100%;
`;

const ReferenceListHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 20px;
    margin-bottom: 8px;
`;

const ReferenceListTitleText = styled(SectionHeaderTitle)``;

const ReferenceListParamsWrap = styled.div`
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;

const ReferenceFilterTagItem = styled(ReferenceLinkTagItem)`
    gap: 6px;
    justify-content: flex-start;
    max-width: 260px;
    height: 24px;
    padding: 0 7px;
    border: solid 1px var(--color-Line);
    transition: all 120ms ease-in;
    background-color: var(--color-Base1);
    cursor: pointer;

    &:hover {
        background-color: var(--color-ButtonHover4);
        border-color: var(--color-Line);
    }

    &.active {
        background-color: var(--color-TagActive);
        border-color: #9fd0e4;
    }
`;

const ReferenceFilterTagIconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
`;

const ReferenceFilterTagName = styled(TagText)`
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
`;

const ReferenceFilterTagCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 18px;
    height: 16px;
    padding: 0 5px;
    border-radius: 999px;
    background-color: rgba(255, 255, 255, 0.76);
    color: var(--color-SubBlack);
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1;
`;

const ReferenceFilterTagAction = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
`;

const ReferenceListContent = styled.div``;

const ReferenceListBlankContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin: 80px;
`;

const ReferenceListBlankText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const ReferenceListItemRow = styled.div`
    padding: 8px 0px;
    border-bottom: solid 1px var(--color-Background);
    cursor: default;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        transition: all 80ms ease-in;
        cursor: pointer;
        background-color: var(--color-ListHover);
    }
`;

const ReferenceListItemTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const ReferenceListItemText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const ThreadListPanel = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: var(--color-White);
`;

const LeftPanelSection = styled.section`
    display: flex;
    flex-direction: column;
`;

const LeftPanelStatusSection = styled(LeftPanelSection)`
    flex: 0 0 auto;
    padding: 8px 12px;
    border-bottom: dashed 1px var(--color-Outline);
`;

const LeftPanelClassificationSection = styled(LeftPanelSection)`
    flex: 1;
    min-height: 0;
    padding: 16px 12px;
    overflow-y: auto;
`;

const ClassificationContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-top: 8px;
`;

const LeftPanelViewButton = styled.button`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 0;
    height: 26px;
    padding: 0 10px;
    border: 0;
    border-radius: 3px;
    background-color: ${({ $active }) => ($active ? "color-mix(in srgb, var(--color-Key) 8%, white)" : "transparent")};
    color: ${({ $active }) => ($active ? "var(--color-Key)" : "var(--color-Grey1)")};
    font-size: 1.2rem;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    cursor: pointer;
    line-height: 1;
    white-space: nowrap;
    transition: color 160ms ease-in-out;

    &:hover:not(:disabled) {
        color: ${({ $active }) => ($active ? "var(--color-Key)" : "var(--color-SubBlack)")};
        background-color: ${({ $active }) =>
            $active ? "color-mix(in srgb, var(--color-Key) 8%, white)" : "var(--color-Base1)"};
    }

    min-width: 44px;

    & + & {
        border-left: 1px solid var(--color-Line);
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
`;

const LeftPanelViewToggle = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    width: max-content;
    padding: 1px;
    border: 1px solid var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
    flex-shrink: 0;
`;

function ReferenceDetailPage(props) {
    // 라우터 파라미터
    const { space_uuid, reference_no } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const referenceNo = Number(reference_no);
    const [activeMainView, setActiveMainView] = useState(searchParams.get("view") === "pdf" ? "pdf" : "metadata");
    const [pdfToolbarCenterTarget, setPdfToolbarCenterTarget] = useState(null);
    const [pdfToolbarRightTarget, setPdfToolbarRightTarget] = useState(null);

    // 사용자 데이터
    const [user, setUser] = useState({});
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    // 레퍼런스 데이터
    const referenceQuery = useReference({ reference_no: referenceNo });
    const [reference, setReference] = useState({});
    useEffect(() => {
        if (referenceQuery.data) {
            setReference(referenceQuery.data);
        }
    }, [referenceQuery.data]);

    useEffect(() => {
        if (!Number.isInteger(referenceNo) || referenceNo < 1) {
            navigate("/error", { replace: true });
        }
    }, [referenceNo, navigate]);

    useEffect(() => {
        setActiveMainView(searchParams.get("view") === "pdf" ? "pdf" : "metadata");
    }, [searchParams]);

    const updateReferenceMutation = useReferenceUpdate();
    const deleteReferenceMutation = useReferenceDelete();
    const abstractTextareaRef = useRef(null);
    const memoTextareaRef = useRef();
    const [showCitationStyleModal, setShowCitationStyleModal] = useState(false);
    const [showFolderEditModal, setShowFolderEditModal] = useState(false);
    const [showTagEditModal, setShowTagEditModal] = useState(false);
    const [showBibInfoEditModal, setShowBibInfoEditModal] = useState(false);

    const handleReferenceFieldChanged = useCallback((event) => {
        const { name, value } = event.target;
        // bib_json 내부 필드는 "bib_json.필드명" 형태로 name을 넣어줄 수 있음
        if (name.startsWith("bib_json.")) {
            setReference((prev) => ({
                ...prev,
                bib_json: {
                    ...prev.bib_json,
                    [name.replace("bib_json.", "")]: value,
                },
            }));
            return;
        }
        setReference((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const updateReferenceField = useCallback(
        (fields) => {
            if (!reference.id) return;
            const updateData = { id: reference.id };
            Object.entries(fields).forEach(([key, value]) => {
                if (key.startsWith("bib_json.")) {
                    if (!updateData.bib_json) updateData.bib_json = { ...reference.bib_json };
                    updateData.bib_json[key.replace("bib_json.", "")] = value;
                    return;
                }
                updateData[key] = value;
            });
            updateReferenceMutation.mutate(updateData);
        },
        [reference.bib_json, reference.id, updateReferenceMutation],
    );

    const handleDeleteButtonClick = useCallback(() => {
        let message = "참고문헌을 삭제하시겠습니까?";
        if (reference.citation_count > 0) {
            message = "연구노트에 인용된 참고문헌입니다. 삭제하시겠습니까?";
        }
        Confirm("warn", "참고문헌 삭제", message, "삭제", () => {
            deleteReferenceMutation.mutate({
                ids: [reference.id],
            });
            navigate(`/${space_uuid}/reference`);
        });
    }, [deleteReferenceMutation, navigate, reference.citation_count, reference.id, space_uuid]);

    // 폴더
    const folderQuery = useNavigatorFolders();
    const [referenceFolder, setReferenceFolder] = useState(null);
    useEffect(() => {
        if (folderQuery.data || reference.folder) {
            setReferenceFolder(folderQuery.data?.find((folder) => folder.id === reference.folder) || null);
        }
    }, [folderQuery.data, reference.folder]);

    useEffect(() => {
        const textarea = memoTextareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [reference?.memo]);

    const [referenceList, setReferenceList] = useState([]);
    const [referenceListParams, setReferenceListParams] = useState(null);
    const previousReferenceFilterRef = useRef(null);
    const referenceListQuery = useReferenceList(referenceListParams);
    const { data: referenceListData, refetch: refetchReferenceList } = referenceListQuery;

    useEffect(() => {
        if (reference?.id && referenceListParams === null) {
            setReferenceListParams({
                ...DEFAULT_REFERENCE_LIST_PARAMS,
                folder: reference.folder,
                tags: reference.tag_mappings?.map((tag) => tag.id).join(",") || "",
            });
        }
    }, [reference, referenceListParams]);

    useEffect(() => {
        if (!reference?.id) return;

        const currentFilter = {
            referenceId: reference.id,
            folder: reference.folder || null,
            tags: reference.tag_mappings?.map((tag) => tag.id) || [],
        };
        const previousFilter = previousReferenceFilterRef.current;

        if (!previousFilter || previousFilter.referenceId !== reference.id || referenceListParams === null) {
            previousReferenceFilterRef.current = currentFilter;
            return;
        }

        const previousTagSet = new Set(previousFilter.tags);
        const currentTagSet = new Set(currentFilter.tags);

        const addedTagIds = currentFilter.tags.filter((tagId) => !previousTagSet.has(tagId));
        const removedTagIds = previousFilter.tags.filter((tagId) => !currentTagSet.has(tagId));
        const folderChanged = previousFilter.folder !== currentFilter.folder;

        if (folderChanged || addedTagIds.length > 0 || removedTagIds.length > 0) {
            setReferenceListParams((prev) => {
                if (!prev) return prev;

                const nextTagIds = new Set(
                    (prev.tags || "")
                        .split(",")
                        .filter((tagId) => !!tagId)
                        .map((tagId) => Number(tagId)),
                );
                removedTagIds.forEach((tagId) => nextTagIds.delete(tagId));
                addedTagIds.forEach((tagId) => nextTagIds.add(tagId));

                return {
                    ...prev,
                    folder: folderChanged ? currentFilter.folder : prev.folder,
                    tags: Array.from(nextTagIds).join(","),
                };
            });
        }

        previousReferenceFilterRef.current = currentFilter;
    }, [reference, referenceListParams]);

    useEffect(() => {
        if (referenceListParams) {
            refetchReferenceList();
        }
    }, [referenceListParams, refetchReferenceList]);

    useEffect(() => {
        if (referenceListData) {
            setReferenceList(referenceListData.results || []);
        }
    }, [referenceListData]);

    const handleBackButtonClick = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate(`/${space_uuid}/reference`);
    }, [navigate, space_uuid]);

    // region: page 이동 처리
    const [curPage, setCurPage] = useState(() => Math.max(1, Number(searchParams.get("page")) || 1));
    const [maxPage, setMaxPage] = useState(1);

    const handleInputPageChanged = useCallback(
        (newPage) => {
            if (newPage < 1) newPage = 1;
            if (maxPage > 1 && newPage > maxPage) newPage = maxPage;
            if (newPage === curPage) return;

            setCurPage(newPage);
        },
        [curPage, maxPage],
    );

    // region: 리뷰 박스 처리
    const [boxes, setBoxes] = useState([]);
    const [selectedBox, setSelectedBox] = useState(null);
    const [searchBox, setSearchBox] = useState(null);

    // region: 웹소켓 훅
    const {
        threads,
        isConnected: isReferenceReviewConnected,
        isThreadListLoading,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        pdfMetadataStatus,
        chatStart,
        chatStop,
        chatDelete,
        threadUpdateTitle,
        myProfileId,
    } = useReferenceReviewWebSocket({
        reference_uuid: reference.uuid,
        user,
        connectionDependencies: [reference.uuid, userQuery.data],
        onFileUploaded: () => {
            referenceQuery.refetch();
        },
        onFileDeleted: () => {
            referenceQuery.refetch();
            navigate(`/${space_uuid}/reference/${reference_no}`, { replace: true });
        },
        onPdfMetadata: () => {
            referenceQuery.refetch();
        },
        onCreateReviewBox: (review_selection_data) => {
            if (review_selection_data.page === curPage) {
                setBoxes((prevBoxes) => [
                    ...prevBoxes.filter((box) => box.id !== Constants.REVIEW_NEW_SELECTION_ID),
                    { ...review_selection_data },
                ]);
            }
        },
        onStreamStart: (streaming_message_id) => {
            setFocusedMessageId(streaming_message_id);
        },
    });
    useEffect(() => {
        const reviewSelections = new Map();
        threads
            .filter((thread) => !thread.is_deleted)
            .forEach((thread) => {
                thread.messages
                    .filter(
                        (message) =>
                            message.is_deleted === false &&
                            message.review_selection &&
                            message.review_selection.file === reference.file_id,
                    )
                    .forEach((message) => {
                        reviewSelections.set(message.review_selection.id, message.review_selection);
                    });
            });

        setBoxes((prevBoxes) => [
            ...Array.from(reviewSelections.values()),
            ...prevBoxes.filter((box) => box.id === Constants.REVIEW_NEW_SELECTION_ID),
        ]);
    }, [reference.file_id, threads]);

    const selectedThreadLastVisibleMessageId = useMemo(() => {
        if (!selectedThread?.messages?.length) return null;

        const messageMapByParent = {};
        selectedThread.messages.forEach((message) => {
            const parent = message.parent || null;
            if (!messageMapByParent[parent]) {
                messageMapByParent[parent] = [];
            }
            messageMapByParent[parent].push(message);
        });

        let lastVisibleMessageId = null;
        let parentId = null;
        while (true) {
            const children = messageMapByParent[parentId];
            if (!children || children.length === 0) break;

            const message = children[children.length - 1];
            if (message.is_deleted) break;

            lastVisibleMessageId = message.id;
            parentId = message.id;
        }

        return lastVisibleMessageId;
    }, [selectedThread]);

    // region: 새 창으로 리뷰 진입 처리
    const [pendingThreadId, setPendingThreadId] = useState(() => searchParams.get("thread"));
    const [pendingMessageId, setPendingMessageId] = useState(() => searchParams.get("message"));
    const [pendingSelectionId, setPendingSelectionId] = useState(() => searchParams.get("selection"));
    const [focusedMessageId, setFocusedMessageId] = useState(null);
    useEffect(() => {
        if (!pendingThreadId || !threads.length) return;
        const found = threads.find((thread) => String(thread.id) === String(pendingThreadId));
        if (!found) {
            setPendingThreadId(null);
            setPendingMessageId(null);
            setPendingSelectionId(null);
            return;
        }

        setSelectedThread(found);
        const message = found.messages?.find((item) => String(item.id) === String(pendingMessageId));
        const selection = found.messages
            ?.map((item) => item.review_selection)
            .find((item) => String(item?.id) === String(pendingSelectionId));
        setFocusedMessageId(message?.id || null);
        if (selection) {
            setCurPage(selection.page);
            setSearchBox({ ...selection });
        }
        setPendingThreadId(null);
        setPendingMessageId(null);
        setPendingSelectionId(null);
    }, [pendingMessageId, pendingSelectionId, pendingThreadId, setSelectedThread, threads]);

    const handleSelectReviewMessage = useCallback(
        (message) => {
            if (!message?.id) return;

            const targetThread = threads
                .filter((thread) => !thread.is_deleted)
                .find((thread) => thread.messages.some((threadMessage) => threadMessage.id === message.id));

            if (!targetThread) return;

            setSelectedBox(null);
            setSearchBox(null);
            setSelectedThread(targetThread);
            setFocusedMessageId(null);
            requestAnimationFrame(() => setFocusedMessageId(message.id));
        },
        [setSelectedThread, threads],
    );

    const referenceListParamsRenderer = () => {
        const paramELs = [];
        if (referenceList && referenceListParams && referenceFolder) {
            const isActive = referenceFolder.id === referenceListParams.folder;
            const referenceFolderCount = referenceList.filter((r) => r.folder === referenceFolder.id).length;
            paramELs.push(
                <Tooltip key="folder" message={isActive ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}>
                    <ReferenceFilterTagItem
                        className={isActive ? "active" : ""}
                        $margin={"0 8px 2px 0"}
                        onClick={() => {
                            if (isActive) {
                                setReferenceListParams((prev) => ({ ...prev, folder: null }));
                            } else if (referenceListParams.folder === null) {
                                setReferenceListParams((prev) => ({ ...prev, folder: referenceFolder.id }));
                            }
                        }}
                    >
                        <ReferenceFilterTagIconSlot>
                            <Icon name="folder" size="10" color="var(--color-SubBlack)" />
                        </ReferenceFilterTagIconSlot>
                        <ReferenceFilterTagName>{referenceFolder.name}</ReferenceFilterTagName>
                        <ReferenceFilterTagCount>{referenceFolderCount}</ReferenceFilterTagCount>
                        <ReferenceFilterTagAction>
                            <Icon name={isActive ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                        </ReferenceFilterTagAction>
                    </ReferenceFilterTagItem>
                </Tooltip>,
            );
        }

        if (referenceList && referenceListParams && reference.tag_mappings) {
            const tags = (referenceListParams.tags || "")
                .split(",")
                .filter((tagId) => !!tagId)
                .map((tagId) => reference.tag_mappings.find((item) => item.id === Number(tagId)))
                .filter(Boolean);

            paramELs.push(
                ...reference.tag_mappings.map((tag, index) => {
                    const tagIds = tags.map((item) => item.id);
                    const isActive = tagIds.includes(tag.id);
                    const tagReferenceCount = referenceList.filter((r) =>
                        r.tag_mappings.map((t) => t.id).includes(tag.id),
                    ).length;
                    return (
                        <Tooltip
                            key={index + "TAG_FILTER"}
                            message={isActive ? "클릭하여 필터 해제" : "클릭하여 필터 적용"}
                        >
                            <ReferenceFilterTagItem
                                className={isActive ? "active" : ""}
                                $margin={"0 8px 2px 0"}
                                onClick={() => {
                                    if (isActive) {
                                        setReferenceListParams((prev) => ({
                                            ...prev,
                                            tags: prev.tags
                                                .split(",")
                                                .filter((id) => Number(id) !== tag.id)
                                                .join(","),
                                        }));
                                    } else {
                                        tagIds.push(tag.id);
                                        setReferenceListParams((prev) => ({ ...prev, tags: tagIds.join(",") }));
                                    }
                                }}
                            >
                                <ReferenceFilterTagIconSlot>
                                    <Icon name="tag" size="10" color="var(--color-SubBlack)" />
                                </ReferenceFilterTagIconSlot>
                                <ReferenceFilterTagName>{tag.name}</ReferenceFilterTagName>
                                <ReferenceFilterTagCount>{tagReferenceCount}</ReferenceFilterTagCount>
                                <ReferenceFilterTagAction>
                                    <Icon name={isActive ? "x" : "plus"} size="8" color="var(--color-SubBlack)" />
                                </ReferenceFilterTagAction>
                            </ReferenceFilterTagItem>
                        </Tooltip>
                    );
                }),
            );
        }

        return paramELs;
    };

    const renderViewToggle = () => (
        <LeftPanelViewToggle $activeView={activeMainView}>
            <LeftPanelViewButton
                type="button"
                $active={activeMainView === "metadata"}
                onClick={() => setActiveMainView("metadata")}
            >
                정보
            </LeftPanelViewButton>
            <LeftPanelViewButton
                type="button"
                $active={activeMainView === "pdf"}
                onClick={() => setActiveMainView("pdf")}
            >
                파일
            </LeftPanelViewButton>
        </LeftPanelViewToggle>
    );

    const renderToolbarDateInfo = () => (
        <DateSection>
            <DateText>
                추가 {getDateFromString(reference.created_at)} {getTimeFromString(reference.created_at)}
            </DateText>
            <DateText>|</DateText>
            <DateText>
                최근 활동 {getDateFromString(reference.updated_at)} {getTimeFromString(reference.updated_at)}
            </DateText>
        </DateSection>
    );

    const renderClassificationContent = () => (
        <ClassificationContent>
            <ReferenceLinkItem>
                <ReferenceLinkItemHeader>
                    <ReferenceLinkItemHeaderTitle>폴더</ReferenceLinkItemHeaderTitle>
                    <Tooltip message={"폴더 연결"}>
                        <Button
                            {...BUTTON_STYLES.iconGhost}
                            buttonSize={"20px"}
                            onClick={() => setShowFolderEditModal(true)}
                            disabled={isReadonlyUser(user)}
                        >
                            <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                        </Button>
                    </Tooltip>
                </ReferenceLinkItemHeader>
                {referenceFolder && referenceList ? (
                    <ReferenceLinkTagItemWrap>
                        <ReferenceLinkTagItem $margin={"0 8px 2px 0"}>
                            <TagText>{referenceFolder.name}</TagText>
                        </ReferenceLinkTagItem>
                    </ReferenceLinkTagItemWrap>
                ) : (
                    <ReferenceLinkItemBlankBox>
                        <ReferenceLinkItemBlankText>연결된 폴더가 없습니다.</ReferenceLinkItemBlankText>
                    </ReferenceLinkItemBlankBox>
                )}
            </ReferenceLinkItem>
            <ReferenceLinkItem>
                <ReferenceLinkItemHeader>
                    <ReferenceLinkItemHeaderTitle>태그</ReferenceLinkItemHeaderTitle>
                    <Tooltip message={"태그 연결"}>
                        <Button
                            {...BUTTON_STYLES.iconGhost}
                            buttonSize={"20px"}
                            onClick={() => setShowTagEditModal(true)}
                            disabled={isReadonlyUser(user)}
                        >
                            <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                        </Button>
                    </Tooltip>
                </ReferenceLinkItemHeader>
                {reference?.tag_mappings?.length > 0 && referenceList ? (
                    <ReferenceLinkTagItemWrap>
                        {reference.tag_mappings.map((tag, index) => (
                            <ReferenceLinkTagItem key={index + "TAG"} $margin={"0 8px 2px 0"}>
                                <TagText ellipsis>{tag.name}</TagText>
                            </ReferenceLinkTagItem>
                        ))}
                    </ReferenceLinkTagItemWrap>
                ) : (
                    <ReferenceLinkItemBlankBox>
                        <ReferenceLinkItemBlankText>연결된 태그가 없습니다.</ReferenceLinkItemBlankText>
                    </ReferenceLinkItemBlankBox>
                )}
            </ReferenceLinkItem>
        </ClassificationContent>
    );

    const renderThreadListPanel = () => (
        <ThreadListPanel>
            <LeftPanelStatusSection>
                <ReferenceFileAnalysisView
                    reference={reference}
                    pdfIndexingInfo={pdfIndexingInfo}
                    chatStart={chatStart}
                    user={user}
                    onOpenFile={() => setActiveMainView("pdf")}
                />
            </LeftPanelStatusSection>
            <LeftPanelClassificationSection>{renderClassificationContent()}</LeftPanelClassificationSection>
        </ThreadListPanel>
    );

    const renderReferenceDetailPreview = () => (
        <ReferenceDetailPreview>
            <ReferenceDetailPreviewInner>
                <DetailCard>
                    <BibInfoSection>
                        <SectionHeader>
                            <SectionHeaderTitle>서지정보</SectionHeaderTitle>
                            <Tooltip message={"서지정보 수정"}>
                                <Button
                                    {...BUTTON_STYLES.iconGhost}
                                    buttonSize={"20px"}
                                    onClick={() => setShowBibInfoEditModal(true)}
                                    disabled={isReadonlyUser(user)}
                                >
                                    <Icon name={"modify"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                            <Tooltip message={"인용 스타일"}>
                                <Button
                                    {...BUTTON_STYLES.iconGhost}
                                    buttonSize={"20px"}
                                    onClick={() => setShowCitationStyleModal(true)}
                                >
                                    <Icon name={"list"} color={"var(--color-SubBlack)"} />
                                </Button>
                            </Tooltip>
                        </SectionHeader>
                        {pdfMetadataStatus === "RUNNING" ? (
                            <MetadataExtractionStatus>
                                <Icon name="load" size="12" color="var(--color-Grey1)" />
                                PDF 파일에서 서지정보를 추출 중입니다.
                            </MetadataExtractionStatus>
                        ) : (
                            <BibInfoText>{reference?.styled_text || ""}</BibInfoText>
                        )}
                    </BibInfoSection>
                </DetailCard>

                <DetailCard>
                    <AbstractSection>
                        <SectionHeader>
                            <SectionHeaderTitle>초록</SectionHeaderTitle>
                        </SectionHeader>
                        {pdfMetadataStatus === "RUNNING" ? (
                            <MetadataExtractionStatus>
                                <Icon name="load" size="12" color="var(--color-Grey1)" />
                                PDF 파일에서 초록을 추출 중입니다.
                            </MetadataExtractionStatus>
                        ) : (
                            <AbstractTextarea
                                ref={abstractTextareaRef}
                                placeholder={"PDF 파일을 업로드하여 자동으로 추출할 수 있습니다."}
                                name={"bib_json.abstract"}
                                value={reference?.bib_json?.abstract || ""}
                                onChange={handleReferenceFieldChanged}
                                saveAct={() =>
                                    updateReferenceField({
                                        "bib_json.abstract": reference?.bib_json?.abstract || "",
                                    })
                                }
                                disabled={isReadonlyUser(user)}
                                padding={"2px"}
                            />
                        )}
                    </AbstractSection>
                </DetailCard>

                <DetailCard>
                    <AnnotationRightSection>
                        <MemoSection>
                            <MemoTitleText>메모</MemoTitleText>
                            <MemoDiv>
                                <MemoTextarea
                                    ref={memoTextareaRef}
                                    rows={1}
                                    name={"memo"}
                                    placeholder={"입력된 메모가 없습니다."}
                                    value={reference?.memo || ""}
                                    onChange={handleReferenceFieldChanged}
                                    onKeyDown={(event) => {
                                        genericEnterKeyDown(event, () => {
                                            if (event.shiftKey) {
                                                handleReferenceFieldChanged(event);
                                            } else {
                                                event.preventDefault();
                                                memoTextareaRef.current.blur();
                                            }
                                        });
                                    }}
                                    onBlur={() => {
                                        updateReferenceField({ memo: reference.memo });
                                    }}
                                    disabled={isReadonlyUser(user)}
                                />
                            </MemoDiv>
                        </MemoSection>
                    </AnnotationRightSection>
                </DetailCard>

                <DetailCard>
                    <ReferenceListSection>
                        <ReferenceListHeader>
                            <ReferenceListTitleText>연결된 참고문헌</ReferenceListTitleText>
                            <CountTagItem>
                                <TagText>
                                    {referenceListParams?.folder === null && referenceListParams?.tags === ""
                                        ? 0
                                        : referenceList?.length}{" "}
                                    건
                                </TagText>
                            </CountTagItem>
                        </ReferenceListHeader>
                        <ReferenceListParamsWrap>{referenceListParamsRenderer()}</ReferenceListParamsWrap>
                        <ReferenceListContent>
                            {referenceList &&
                                (referenceList.length === 0 ||
                                (referenceListParams?.folder === null && referenceListParams?.tags === "") ? (
                                    <ReferenceListBlankContainer>
                                        <Icon name={"folder"} size={"32"} color={"var(--color-Grey1)"} />
                                        <ReferenceListBlankText>연결된 참고문헌이 없습니다.</ReferenceListBlankText>
                                    </ReferenceListBlankContainer>
                                ) : (
                                    referenceList.map((item, index) => (
                                        <ReferenceListItemRow
                                            key={index}
                                            onClick={() => {
                                                navigate(`/${space_uuid}/reference/${item.reference_no}`);
                                            }}
                                        >
                                            <ReferenceListItemTitle>{item.display_name}</ReferenceListItemTitle>
                                            <ReferenceListItemText>{item.styled_text}</ReferenceListItemText>
                                        </ReferenceListItemRow>
                                    ))
                                ))}
                        </ReferenceListContent>
                    </ReferenceListSection>
                </DetailCard>
            </ReferenceDetailPreviewInner>
        </ReferenceDetailPreview>
    );

    // region: 마크업
    return (
        <PageLayout>
            <HtmlHead title={reference?.display_name} />
            <ReferenceDetailPageHeader
                reference={reference}
                referenceMutation={updateReferenceMutation}
                rightContent={
                    <>
                        <Tooltip message={"즐겨찾기"}>
                            <Button
                                {...BUTTON_STYLES.iconSurfaceStatic}
                                border={
                                    reference?.is_favorite
                                        ? "solid 2px var(--color-Key)"
                                        : "solid 2px var(--color-DisabledButton)"
                                }
                                onClick={() => updateReferenceField({ is_favorite: !reference.is_favorite })}
                                disabled={isReadonlyUser(user)}
                            >
                                <Icon
                                    name={"bookmark"}
                                    size={"12"}
                                    color={reference?.is_favorite ? "var(--color-Key)" : "var(--color-DisabledButton)"}
                                />
                            </Button>
                        </Tooltip>
                        <Tooltip message={"참고문헌 삭제"}>
                            <Button
                                onlyIcon
                                {...BUTTON_STYLES.actionPrimary}
                                onClick={handleDeleteButtonClick}
                                disabled={isReadonlyUser(user) && !isAdminUser(user)}
                            >
                                <Icon name={"delete"} size={"12"} color={"var(--color-White)"} />
                            </Button>
                        </Tooltip>
                    </>
                }
            />
            <SpaceDetailPageLayout
                headerHeight={Constants.TABLET_OR_PC_HEADER_HEIGHT}
                mainBackgroundColor={"var(--color-White)"}
                mainMinWidth={640}
                navigate={navigate}
                onBack={handleBackButtonClick}
                sidebarContent={renderThreadListPanel()}
                sidebarTitle={"상세보기"}
                sidebarTitleLabel={"참고문헌"}
                spaceUuid={space_uuid}
                user={user}
                webSocketConnected={isReferenceReviewConnected}
                webSocketConnectionReady={Boolean(reference.uuid && userQuery.data)}
            >
                <ReferenceDetailPageLayout
                    showPanelRails={true}
                    main={
                        <ReviewMainInner>
                            <ReviewToolbar>
                                <ReviewToolbarLeft>{renderViewToggle()}</ReviewToolbarLeft>
                                <ReviewToolbarCenter ref={setPdfToolbarCenterTarget} />
                                <ReviewToolbarRight ref={setPdfToolbarRightTarget}>
                                    {activeMainView === "metadata" && renderToolbarDateInfo()}
                                </ReviewToolbarRight>
                            </ReviewToolbar>
                            <ReviewViewContent>
                                {activeMainView === "metadata" ? (
                                    renderReferenceDetailPreview()
                                ) : !reference?.file ? (
                                    <PdfReaderContainer>
                                        <ReferenceFileUploadView user={user} reference={reference} />
                                    </PdfReaderContainer>
                                ) : (
                                    <PdfReaderContainer>
                                        <ReferenceFilePdfView
                                            fileUrl={reference.file}
                                            spaceUuid={space_uuid}
                                            referenceFileUuid={reference.file_uuid}
                                            page={curPage}
                                            maxPage={maxPage}
                                            boxes={boxes}
                                            setBoxes={setBoxes}
                                            threads={threads}
                                            selectedBox={selectedBox}
                                            setSelectedBox={setSelectedBox}
                                            searchBox={searchBox}
                                            setSearchBox={setSearchBox}
                                            setMaxPage={setMaxPage}
                                            onPageChange={handleInputPageChanged}
                                            chatStart={chatStart}
                                            parentMessageId={selectedThreadLastVisibleMessageId}
                                            profiles={profiles}
                                            onSelectReviewMessage={handleSelectReviewMessage}
                                            toolbarCenterTarget={pdfToolbarCenterTarget}
                                            toolbarRightTarget={pdfToolbarRightTarget}
                                        />
                                    </PdfReaderContainer>
                                )}
                            </ReviewViewContent>
                        </ReviewMainInner>
                    }
                    sidebar={
                        <ReferenceDetailSidebar
                            user={user}
                            myProfileId={myProfileId}
                            reference={reference}
                            threads={threads}
                            selectedThread={selectedThread}
                            setSelectedThread={setSelectedThread}
                            profiles={profiles}
                            setCurPage={setCurPage}
                            selectedBox={selectedBox}
                            setSelectedBox={setSelectedBox}
                            searchBox={searchBox}
                            setSearchBox={setSearchBox}
                            chatStart={chatStart}
                            chatStop={chatStop}
                            chatDelete={chatDelete}
                            threadUpdateTitle={threadUpdateTitle}
                            streamingMessageIds={streamingMessageIds}
                            autoScrollToEnd={autoScrollToEnd}
                            setAutoScrollToEnd={setAutoScrollToEnd}
                            pdfIndexingInfo={pdfIndexingInfo}
                            focusedMessageId={focusedMessageId}
                            isThreadListLoading={isThreadListLoading}
                        />
                    }
                ></ReferenceDetailPageLayout>
            </SpaceDetailPageLayout>
            {showCitationStyleModal && (
                <ReferenceCitationStyleModal exitModal={() => setShowCitationStyleModal(false)} reference={reference} />
            )}
            {showFolderEditModal && (
                <NavigatorFolderEditModal
                    resources={[{ resource_type: "reference", id: reference.id }]}
                    selectedFolderId={reference.folder}
                    exitModal={() => setShowFolderEditModal(false)}
                />
            )}
            {showTagEditModal && (
                <ReferenceTagEditModal
                    referenceId={reference.id}
                    referenceFile={reference.file}
                    selectedTagIds={reference.tag_mappings?.map((tag) => tag.id) || []}
                    exitModal={() => {
                        setShowTagEditModal(false);
                    }}
                />
            )}
            {showBibInfoEditModal && (
                <ReferenceBibInfoEditModal
                    reference={reference}
                    exitModal={() => {
                        setShowBibInfoEditModal(false);
                    }}
                />
            )}
        </PageLayout>
    );
}

export default ReferenceDetailPage;
